import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  Remove,
} from '@mui/icons-material';
import { apiCall } from '../utils/api';

interface RealTimeMetric {
  title: string;
  value: number;
  unit: string;
  previousValue?: number;
  threshold?: { warning: number; error: number };
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

interface RealTimeMetricsProps {
  metrics: RealTimeMetric[];
  refreshInterval?: number; // in milliseconds
  onRefresh?: () => void;
}

const getTrendIcon = (current: number, previous?: number) => {
  if (!previous) return <Remove fontSize="small" />;
  if (current > previous) return <TrendingUp fontSize="small" color="success" />;
  if (current < previous) return <TrendingDown fontSize="small" color="error" />;
  return <Remove fontSize="small" />;
};

const getTrendColor = (current: number, previous?: number) => {
  if (!previous) return 'text.secondary';
  if (current > previous) return 'success.main';
  if (current < previous) return 'error.main';
  return 'text.secondary';
};

const getMetricColor = (value: number, threshold?: { warning: number; error: number }) => {
  if (!threshold) return 'success';
  if (value >= threshold.error) return 'error';
  if (value >= threshold.warning) return 'warning';
  return 'success';
};

const RealTimeMetricCard: React.FC<{ metric: RealTimeMetric }> = ({ metric }) => {
  const trend = metric.previousValue ? ((metric.value - metric.previousValue) / metric.previousValue) * 100 : 0;
  const metricColor = getMetricColor(metric.value, metric.threshold);
  
  return (
    <Card sx={{ minHeight: 120 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="body2" color="textSecondary">
            {metric.title}
          </Typography>
          <Box display="flex" alignItems="center" color={getTrendColor(metric.value, metric.previousValue)}>
            {getTrendIcon(metric.value, metric.previousValue)}
            {metric.previousValue && (
              <Typography variant="caption" ml={0.5}>
                {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
              </Typography>
            )}
          </Box>
        </Box>
        
        <Typography variant="h5" color={`${metricColor}.main`} gutterBottom>
          {typeof metric.value === 'number' && metric.value % 1 !== 0 
            ? metric.value.toFixed(2) 
            : metric.value.toLocaleString()}
          <Typography component="span" variant="body2" color="textSecondary" ml={1}>
            {metric.unit}
          </Typography>
        </Typography>
        
        {metric.threshold && (
          <Box mt={1}>
            <LinearProgress
              variant="determinate"
              value={(metric.value / metric.threshold.error) * 100}
              color={metricColor}
              sx={{ height: 4, borderRadius: 2 }}
            />
            <Box display="flex" justifyContent="space-between" mt={0.5}>
              <Typography variant="caption" color="textSecondary">
                Warning: {metric.threshold.warning}{metric.unit}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Critical: {metric.threshold.error}{metric.unit}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const RealTimeMetrics: React.FC<RealTimeMetricsProps> = ({ 
  metrics, 
  refreshInterval = 30000, 
  onRefresh 
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (onRefresh) {
        await onRefresh();
      }
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to refresh metrics:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(handleRefresh, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, onRefresh]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Real-Time Metrics</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="textSecondary">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </Typography>
          <Tooltip title="Refresh now">
            <IconButton 
              onClick={handleRefresh} 
              disabled={isRefreshing}
              size="small"
            >
              <Refresh sx={{ 
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={2}>
        {metrics.map((metric, index) => (
          <RealTimeMetricCard key={`${metric.title}-${index}`} metric={metric} />
        ))}
      </Box>
    </Box>
  );
};

export default RealTimeMetrics;